import { IAccount, IAddress, IHeadquarters } from '@/models';

export const getAccountHeadquarters = (
  account?: IAccount | null,
): IHeadquarters[] => {
  return (
    account?.settings.headquarters?.filter(({ name }) => Boolean(name)) ?? []
  );
};

export const getDefaultHeadquartersAddress = (
  account?: IAccount | null,
): IAddress | null => {
  const headquartersAddress = account?.settings.headquarters?.[0]?.address;

  if (headquartersAddress && typeof headquartersAddress !== 'string') {
    return headquartersAddress;
  }

  return account?.settings.hqAddress ?? null;
};

/** Radius of the circle used to prioritise nearby address predictions. */
export const ACCOUNT_BIAS_RADIUS_METERS = 20000; // 20km

/**
 * GeoJSON stores coordinates as [longitude, latitude]. Returns null for a
 * missing or incomplete pair, and for (0, 0) - an address saved without a
 * geocoded point would otherwise bias every search to the Gulf of Guinea.
 */
const toLatLngLiteral = (
  coordinates?: number[] | null,
): google.maps.LatLngLiteral | null => {
  const [lng, lat] = coordinates ?? [];

  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (lat === 0 && lng === 0) return null;

  return { lat, lng };
};

/**
 * Where to prioritise Google address predictions for the current account.
 *
 * Screens with a map restrict predictions to the visible area, but screens
 * without one (creating a CFS from a call record) would otherwise search the
 * whole country. Prefers the account centre point, falls back to the
 * headquarters address, and finally lets Google infer a region from the IP.
 */
export const getAccountLocationBias = (
  account?: IAccount | null,
): google.maps.CircleLiteral | 'IP_BIAS' => {
  const center =
    toLatLngLiteral(account?.settings.center?.coordinates) ??
    toLatLngLiteral(getDefaultHeadquartersAddress(account)?.point?.coordinates);

  return center ? { center, radius: ACCOUNT_BIAS_RADIUS_METERS } : 'IP_BIAS';
};
